import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Loader2, Clock } from 'lucide-react';
import { config } from '../../config';

interface LeadCaptureFormProps {
  heading?: string;
  subheading?: string;
  /** Hero variant: email + URL only, no name. Bottom CTA: adds name field. */
  variant?: 'hero' | 'full';
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export function LeadCaptureForm({
  heading,
  subheading,
  variant = 'full',
}: LeadCaptureFormProps) {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !website.trim()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch(`${config.apiUrl}/v1/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          website: website.trim(),
          ...(name.trim() ? { name: name.trim() } : {}),
        }),
      });

      if (res.status === 429) {
        setStatus('error');
        setErrorMessage('Too many requests. Please try again in a few minutes.');
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setStatus('error');
        setErrorMessage(
          data?.details?.[0]?.message || 'Something went wrong. Please try again.',
        );
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage('Could not connect to server. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={variant === 'hero' ? 'py-4' : 'text-center py-8'}
      >
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="text-xl font-display font-bold text-gray-900 mb-2 text-center">
          You&apos;re in! We&apos;ll be in touch within 24 hours.
        </h3>
        <p className="text-gray-600 max-w-md mx-auto text-center">
          We&apos;re setting up a personalized AI concierge demo on your website.
          You&apos;ll receive a direct link to see it live.
        </p>
      </motion.div>
    );
  }

  return (
    <div className={variant === 'hero' ? '' : 'text-center'}>
      {heading && (
        <h3 className="text-xl font-display font-bold text-gray-800 mb-2">
          {heading}
        </h3>
      )}
      {subheading && (
        <p className="text-gray-500 text-sm mb-6">{subheading}</p>
      )}

      <form onSubmit={handleSubmit} className={variant === 'hero' ? '' : 'max-w-xl mx-auto'}>
        <div className="space-y-3">
          {variant === 'full' && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full px-5 py-3.5 bg-white/70 backdrop-blur-sm border-2 border-gray-200 rounded-2xl transition-all duration-300 focus:ring-4 focus:ring-lasoxp-500/20 focus:border-lasoxp-500 text-gray-800 placeholder-gray-400 text-base font-body shadow-soft"
            />
          )}
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourhotel.com"
            required
            className="w-full px-5 py-3.5 bg-white/70 backdrop-blur-sm border-2 border-gray-200 rounded-2xl transition-all duration-300 focus:ring-4 focus:ring-lasoxp-500/20 focus:border-lasoxp-500 text-gray-800 placeholder-gray-400 text-base font-body shadow-soft"
          />
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://www.yourhotel.com"
            required
            className="w-full px-5 py-3.5 bg-white/70 backdrop-blur-sm border-2 border-gray-200 rounded-2xl transition-all duration-300 focus:ring-4 focus:ring-lasoxp-500/20 focus:border-lasoxp-500 text-gray-800 placeholder-gray-400 text-base font-body shadow-soft"
          />
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-gradient-to-r from-lasoxp-600 to-accent-600 hover:from-lasoxp-700 hover:to-accent-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-8 py-4 rounded-2xl font-display font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-glow"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Get My Free Demo
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </div>

        {status === 'error' && errorMessage && (
          <p className="text-red-600 text-sm mt-3">{errorMessage}</p>
        )}

        <p className="text-xs text-gray-400 mt-3 flex items-center justify-center gap-1.5">
          <Clock className="w-3 h-3" />
          Takes 2 minutes. No credit card. No commitment.
        </p>
      </form>
    </div>
  );
}
